import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon,
  TrashIcon 
} from '@heroicons/react/24/outline';
import TopicDetailModal from '../components/TopicDetailModal';

const EditExam = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Topic detail modal state
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSubjectLabel, setSelectedSubjectLabel] = useState('');

  const [formData, setFormData] = useState({
    examDate: '',
    examType: 'TYT',
    results: {
      turkish: { correct: 0, wrong: 0, blank: 0 },
      math: { correct: 0, wrong: 0, blank: 0 },
      science: { correct: 0, wrong: 0, blank: 0 },
      physics: { correct: 0, wrong: 0, blank: 0 },
      chemistry: { correct: 0, wrong: 0, blank: 0 },
      biology: { correct: 0, wrong: 0, blank: 0 },
      social: { correct: 0, wrong: 0, blank: 0 },
      mathAyt: { correct: 0, wrong: 0, blank: 0 }
    }
  });

  const [nets, setNets] = useState({
    turkish: 0,
    math: 0,
    science: 0,
    physics: 0,
    chemistry: 0,
    biology: 0,
    social: 0,
    mathAyt: 0
  });

  useEffect(() => {
    fetchExam();
  }, [id]);

  useEffect(() => {
    calculateNets();
  }, [formData.results]);

  const fetchExam = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/exams/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const exam = data.exam;
      
      // Initialize results with default values for missing subjects
      const initializedResults = { ...exam.results };
      const allSubjects = exam.examType === 'TYT' 
        ? ['turkish', 'math', 'science', 'social', 'physics', 'chemistry', 'biology', 'history', 'geography', 'philosophy', 'religion']
        : ['mathAyt', 'physics', 'chemistry', 'biology'];
      
      allSubjects.forEach(subject => {
        if (!initializedResults[subject]) {
          initializedResults[subject] = { correct: 0, wrong: 0, blank: 0 };
        }
      });
      
      setFormData({
        examDate: exam.examDate.split('T')[0], // Format date for input
        examType: exam.examType,
        results: initializedResults
      });
    } catch (error) {
      console.error('Error fetching exam:', error);
      alert('Deneme yüklenirken hata oluştu!');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const calculateNets = () => {
    const newNets = {};
    Object.keys(formData.results).forEach((subject) => {
      const { correct, wrong } = formData.results[subject];
      newNets[subject] = Math.max(0, correct - (wrong / 4)).toFixed(2);
    });
    setNets(newNets);
  };

  const handleInputChange = (subject, field, value) => {
    const numValue = parseInt(value) || 0;
    
    setFormData((prev) => ({
      ...prev,
      results: {
        ...prev.results,
        [subject]: {
          ...prev.results[subject],
          [field]: numValue
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.examDate) {
      alert('Lütfen deneme tarihini seçin!');
      return;
    }

    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/exams/${id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      alert('Deneme başarıyla güncellendi!');
      navigate(`/exam/${id}`);
    } catch (error) {
      console.error('Error updating exam:', error);
      alert(error.response?.data?.message || 'Deneme güncellenirken hata oluştu!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/exams/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Deneme başarıyla silindi!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting exam:', error);
      alert('Silme işlemi başarısız!');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const openTopicModal = (subjectKey, subjectLabel) => {
    setSelectedSubject(subjectKey);
    setSelectedSubjectLabel(subjectLabel);
    setTopicModalOpen(true);
  };

  const handleTopicDetailSave = (subject, topicResults) => {
    // Topic details saved, you can add state here if needed
    console.log('Topic details saved:', subject, topicResults);
  };

  const subjectConfig = {
    TYT: [
      { key: 'turkish', label: 'Türkçe', total: 40, icon: '📖', color: 'blue', hasTopics: true, topicSubject: 'TYT-Türkçe' },
      { key: 'math', label: 'Matematik', total: 40, icon: '🔢', color: 'green', hasTopics: true, topicSubject: 'TYT-Matematik' },
      {
        key: 'science',
        label: 'Fen Bilimleri',
        total: 20,
        icon: '🔬',
        color: 'purple',
        hasTopics: false,
        subSubjects: [
          { key: 'physics', label: 'Fizik', total: 7, icon: '⚛️', color: 'blue', hasTopics: true, topicSubject: 'TYT-Fizik' },
          { key: 'chemistry', label: 'Kimya', total: 7, icon: '🧪', color: 'purple', hasTopics: true, topicSubject: 'TYT-Kimya' },
          { key: 'biology', label: 'Biyoloji', total: 6, icon: '🧬', color: 'pink', hasTopics: true, topicSubject: 'TYT-Biyoloji' }
        ]
      },
      {
        key: 'social',
        label: 'Sosyal Bilimler',
        total: 20,
        icon: '🌍',
        color: 'orange',
        hasTopics: false,
        subSubjects: [
          { key: 'history', label: 'Tarih', total: 5, icon: '🏛️', color: 'orange', hasTopics: true, topicSubject: 'TYT-Tarih' },
          { key: 'geography', label: 'Coğrafya', total: 5, icon: '🗺️', color: 'green', hasTopics: true, topicSubject: 'TYT-Coğrafya' },
          { key: 'philosophy', label: 'Felsefe', total: 5, icon: '💭', color: 'purple', hasTopics: true, topicSubject: 'TYT-Felsefe' },
          { key: 'religion', label: 'Din Kültürü', total: 5, icon: '☪️', color: 'blue', hasTopics: true, topicSubject: 'TYT-Din' }
        ]
      }
    ],
    // AYT subjects - bunlar değişmedi, AYT konuları topics tablosunda 'Matematik', 'Fizik' vs. olarak kayıtlı
    AYT: [
      { key: 'mathAyt', label: 'Matematik', total: 40, icon: '🔢', color: 'green', hasTopics: true, topicSubject: 'Matematik' },
      { key: 'physics', label: 'Fizik', total: 14, icon: '⚛️', color: 'blue', hasTopics: true, topicSubject: 'Fizik' },
      { key: 'chemistry', label: 'Kimya', total: 13, icon: '🧪', color: 'purple', hasTopics: true, topicSubject: 'Kimya' },
      { key: 'biology', label: 'Biyoloji', total: 13, icon: '🧬', color: 'pink', hasTopics: true, topicSubject: 'Biyoloji' }
    ]
  };

  const subjects = subjectConfig[formData.examType];
  const totalNet = subjects.reduce((sum, subject) => {
    if (subject.subSubjects) {
      // For subjects with sub-subjects (Fen Bilimleri), sum all sub-subjects
      return sum + subject.subSubjects.reduce((subSum, subSubject) => 
        subSum + parseFloat(nets[subSubject.key] || 0), 0
      );
    }
    return sum + parseFloat(nets[subject.key] || 0);
  }, 0).toFixed(2);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/exam/${id}`}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Detaya Dön
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Denemeyi Düzenle</h1>
              <p className="text-gray-600 mt-1">Deneme bilgilerini güncelleyin</p>
            </div>
            
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 bg-white hover:bg-red-50 transition"
            >
              <TrashIcon className="w-5 h-5 mr-2" />
              Sil
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Genel Bilgiler</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Exam Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deneme Tarihi *
                </label>
                <input
                  type="date"
                  required
                  value={formData.examDate}
                  onChange={(e) => setFormData({ ...formData, examDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Exam Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deneme Türü *
                </label>
                <select
                  value={formData.examType}
                  onChange={(e) => setFormData({ ...formData, examType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="TYT">TYT</option>
                  <option value="AYT">AYT</option>
                </select>
              </div>
            </div>
          </div>

          {/* Total Net Card */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary-100 text-sm font-medium">Toplam Net</p>
                <p className="text-4xl font-bold mt-1">{totalNet}</p>
              </div>
              <CheckCircleIcon className="w-16 h-16 text-primary-200" />
            </div>
          </div>

          {/* Subjects */}
          {subjects.map((subject) => {
            // Main subject without sub-subjects
            if (!subject.subSubjects) {
              const result = formData.results[subject.key];
              const total = subject.total;
              const sum = result.correct + result.wrong + result.blank;
              const remaining = total - sum;

              return (
                <div key={subject.key} className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{subject.icon}</span>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{subject.label}</h3>
                        <p className="text-sm text-gray-500">{total} Soru</p>
                      </div>
                      {subject.hasTopics && (
                        <button
                          type="button"
                          onClick={() => openTopicModal(subject.topicSubject, subject.label)}
                          className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Konu Detayları
                        </button>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Net</p>
                      <p className={`text-2xl font-bold text-${subject.color}-600`}>
                        {nets[subject.key]}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {/* Doğru */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Doğru
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={total}
                        value={result.correct}
                        onChange={(e) => handleInputChange(subject.key, 'correct', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>

                    {/* Yanlış */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yanlış
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={total}
                        value={result.wrong}
                        onChange={(e) => handleInputChange(subject.key, 'wrong', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    {/* Boş */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Boş
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={total}
                        value={result.blank}
                        onChange={(e) => handleInputChange(subject.key, 'blank', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Warning if total exceeds */}
                  {sum > total && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">
                        ⚠️ Toplam soru sayısı {total}'ı geçemez! (Şu an: {sum})
                      </p>
                    </div>
                  )}

                  {/* Remaining questions */}
                  {remaining > 0 && sum <= total && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        ℹ️ Kalan {remaining} soru otomatik olarak boş sayılacak
                      </p>
                    </div>
                  )}
                </div>
              );
            }

            // Subject with sub-subjects (Fen Bilimleri)
            return (
              <div key={subject.key} className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {subject.icon} {subject.label}
                  <span className="text-sm text-gray-500 ml-2">({subject.total} Soru)</span>
                </h3>
                <div className="space-y-4 ml-4">
                  {subject.subSubjects.map((subSubject) => {
                    const result = formData.results[subSubject.key];
                    const total = subSubject.total;
                    const sum = result.correct + result.wrong + result.blank;
                    const remaining = total - sum;

                    return (
                      <div key={subSubject.key} className="border-l-4 border-primary-200 pl-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-base font-medium text-gray-800">
                              {subSubject.icon} {subSubject.label}
                              <span className="text-xs text-gray-500 ml-2">({subSubject.total} Soru)</span>
                            </h4>
                            {subSubject.hasTopics && (
                              <button
                                type="button"
                                onClick={() => openTopicModal(subSubject.topicSubject, subSubject.label)}
                                className="px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Konular
                              </button>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">Net</p>
                            <p className="text-lg font-bold text-primary-600">
                              {nets[subSubject.key]}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Doğru
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={subSubject.total}
                              value={result.correct}
                              onChange={(e) => handleInputChange(subSubject.key, 'correct', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Yanlış
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={subSubject.total}
                              value={result.wrong}
                              onChange={(e) => handleInputChange(subSubject.key, 'wrong', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Boş
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={subSubject.total}
                              value={result.blank}
                              onChange={(e) => handleInputChange(subSubject.key, 'blank', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Warning if total exceeds */}
                        {sum > total && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-xs text-red-800">
                              ⚠️ Toplam {total}'ı geçemez! (Şu an: {sum})
                            </p>
                          </div>
                        )}

                        {/* Remaining questions */}
                        {remaining > 0 && sum <= total && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-xs text-blue-800">
                              ℹ️ Kalan {remaining} soru boş sayılacak
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/exam/${id}`)}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Güncelleniyor...' : 'Güncelle'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <TrashIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              Denemeyi Sil
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Bu denemeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Detail Modal */}
      <TopicDetailModal
        isOpen={topicModalOpen}
        onClose={() => setTopicModalOpen(false)}
        examId={id}
        subject={selectedSubject}
        subjectLabel={selectedSubjectLabel}
        onSave={handleTopicDetailSave}
      />
    </div>
  );
};

export default EditExam;
